import clsx from 'clsx';
import { Slider } from 'radix-ui';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type SliderMark = {
  /** Mark position value */
  value: number;
};

type SliderProps = {
  /** Current slider value(s). Array length determines number of thumbs (1 = single, 2+ = range) */
  value: number[];
  /** Callback when slider value(s) change */
  onValueChange: (value: number[]) => void;
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Step increment */
  step?: number;
  /** Disable the slider */
  disabled?: boolean;
  /** Accessible label for the slider */
  'aria-label'?: string;
  /** Tick marks to display on the slider */
  marks?: SliderMark[];
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Calculate the percentage position of a mark within the slider range.
 * When all marks share a single value (min === max), returns 0 to avoid division by zero producing NaN.
 */
const getMarkPercentage = (markValue: number, min: number, max: number): number => {
  if (min === max) return 0;
  return ((markValue - min) / (max - min)) * 100;
};

/**
 * Generic multi-value slider component.
 * Supports single (1 thumb) or range (2+ thumbs) selection.
 * Styling extracted from proven LayerItem opacity slider patterns.
 *
 * @example
 * ```tsx
 * // Single value
 * <Slider value={[0.8]} onValueChange={([v]) => setOpacity(v)} min={0} max={1} step={0.01} />
 *
 * // Range (2 thumbs) with marks
 * const marks = [{ value: 100 }, { value: 150 }, { value: 200 }];
 * <Slider value={[148, 168]} onValueChange={setRlRange} min={100} max={200} step={1} marks={marks} />
 * ```
 */
const SliderComponent = ({
  value,
  onValueChange,
  min,
  max,
  step = 1,
  disabled,
  'aria-label': ariaLabel,
  marks,
}: SliderProps) => {
  return (
    <div className="relative w-full">
      <Slider.Root
        className="relative flex h-1 w-full touch-none select-none items-center"
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        aria-label={ariaLabel}
      >
        <Slider.Track className="relative h-1 grow rounded-full bg-muted">
          <Slider.Range className="absolute h-full rounded-full bg-primary" />
          {marks?.map((mark) => {
            const percentage = getMarkPercentage(mark.value, min, max);
            return (
              <div
                key={`mark-${mark.value}`}
                className="absolute top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted"
                style={{ left: `${percentage}%` }}
                aria-hidden="true"
              />
            );
          })}
        </Slider.Track>
        {value.map((_, idx) => (
          <Slider.Thumb
            // biome-ignore lint/suspicious/noArrayIndexKey: Index is stable because thumbs are not added/removed dynamically
            key={`thumb-${idx}`}
            className={clsx(
              'block size-4 cursor-grab rounded-full border border-border bg-background shadow-md',
              'focus:outline-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:cursor-grabbing',
            )}
            aria-label={ariaLabel ? `${ariaLabel} value ${idx + 1}` : `Slider value ${idx + 1}`}
          />
        ))}
      </Slider.Root>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { SliderMark, SliderProps };
export { SliderComponent as Slider };
